import {
  Component,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { RegisterApiService, PowerStation } from '../../core/services/register-api.service';
import { environment } from '../../../environments/environment';
import {
  GoogleMapsLocationService,
  InteractiveLocationMapHandle,
  PreviewLocationMapHandle,
} from '../../core/services/google-maps-location.service';
import Swal from 'sweetalert2';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  customClass: {
    popup: 'premium-toast-popup',
    title: 'premium-toast-title',
  },
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer);
    toast.addEventListener('mouseleave', Swal.resumeTimer);
  },
});

@Component({
  selector: 'app-trade-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './trade-register.component.html',
  styleUrls: ['../register.component.scss', './trade-register.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TradeRegisterComponent implements OnInit, OnDestroy, AfterViewInit {
  registerForm: FormGroup;
  activeStep = 0;
  totalSteps = 7;
  showFormPreview = false;
  Object = Object;

  // Stepper visibility
  showStepper = true;
  stepperReady = false;

  @ViewChild('locationMapContainer') locationMapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('locationSearch') locationSearch?: ElementRef<HTMLInputElement>;
  @ViewChild('previewMapContainer') previewMapContainer?: ElementRef<HTMLDivElement>;

  locationMapError = '';
  previewMapError = '';
  private readonly defaultMapCenter = { lat: 11.1271, lng: 78.6569 };
  private readonly defaultMapZoom = 7;
  private readonly selectedMapZoom = 16;
  private readonly addressStepIndex = 2;
  private readonly previewStepIndex = 6;
  private locationMap: InteractiveLocationMapHandle | null = null;
  private previewLocationMap: PreviewLocationMapHandle | null = null;
  private locationMapInitTimer: ReturnType<typeof setTimeout> | null = null;
  private previewMapInitTimer: ReturnType<typeof setTimeout> | null = null;

  // API Data
  powerStations: PowerStation[] = [];
  stationsLoading = false;

  ashCategories: any[] = [];
  categoriesLoading = false;

  ashUnloadList: any[] = [];
  unloadLoading = false;

  tariffList: any[] = [];
  tariffLoading = false;

  states: any[] = [];
  statesLoading = false;

  districts: any[] = [];
  districtsLoading = false;

  taluks: any[] = [];
  taluksLoading = false;

  villages: any[] = [];
  villagesLoading = false;

  // Track file upload states
  uploadedFiles: { [key: string]: File | null } = {
    gstCertificate: null,
    panCertificate: null,
    identityProof: null,
    leaseDeed: null,
    propertyTax: null,
    installationPermission: null,
    tnebCertificate: null,
  };

  uploadedFileIds: { [key: string]: string | null } = {
    gstCertificate: null,
    panCertificate: null,
    capacityCertificate: null,
    tradeLicence: null,
    leaseDeed: null,
    propertyTax: null,
    installationPermission: null,
    tnebCertificate: null,
  };

  docLabels: { [key: string]: string } = {
    gstCertificate: 'GST Certificate',
    panCertificate: 'PAN Certificate',
    capacityCertificate: 'Existence & Capacity Certificate',
    tradeLicence: 'Trade License for Flyash Copy',
    leaseDeed: 'Lease Deed / Legal Occupancy Certificate',
    propertyTax: 'Property Tax Receipt',
    installationPermission: 'Installation Permission Document',
    tnebCertificate: 'TNEB SC Document',
  };

  documentTypeIds: { [key: string]: number } = {
    gstCertificate: 1,
    panCertificate: 2,
    capacityCertificate: 20,
    tradeLicence: 19,
    leaseDeed: 21,
    propertyTax: 22,
    installationPermission: 23,
  };

  activeUploadDocKey: string | null = null;
  selectedFileForUpload: File | null = null;
  isUploading = false;

  // Multi-select state for Step 4
  showCategoryDropdown = false;
  showUnloadDropdown = false;
  selectedUnloadIds: number[] = [];

  // OTP Flow
  mobileCheckStatus: 'checking' | 'exists' | 'valid' | null = null;
  mobileCheckErrorMsg: string = '';
  mobileOtpForm: FormGroup;
  mobileOtpSent = false;
  mobileOtpVerified = false;
  mobileOtpGenerating = false;
  mobileOtpVerifying = false;
  mobileOtpResendLoading = false;
  mobileOtpError = '';
  mobileOtpSuccess = '';
  mobileOtpResendCountdown = 0;
  otpVerifiedMobile = '';
  readonly mobileOtpFields = ['otp1', 'otp2', 'otp3', 'otp4'] as const;
  private mobileOtpResendTimerId: ReturnType<typeof setInterval> | null = null;
  private readonly MOBILE_OTP_RESEND_SECONDS = 60;

  // Email OTP Flow
  emailOtpForm: FormGroup;
  emailOtpSent = false;
  emailOtpVerified = false;
  emailOtpGenerating = false;
  emailOtpVerifying = false;
  emailOtpResendLoading = false;
  emailOtpError = '';
  emailOtpSuccess = '';
  emailOtpResendCountdown = 0;
  otpVerifiedEmail = '';
  readonly emailOtpFields = ['otp1', 'otp2', 'otp3', 'otp4'] as const;
  private emailOtpResendTimerId: ReturnType<typeof setInterval> | null = null;
  private readonly EMAIL_OTP_RESEND_SECONDS = 60;

  // Submission
  isSubmitting = false;

  // Draft
  isSavingDraft = false;
  isLoadingDraft = false;

  // Vendor Verification
  verifiedVendorMobiles = new Set<string>();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private registerApiService: RegisterApiService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private googleMapsLocationService: GoogleMapsLocationService,
  ) {
    this.registerForm = this.fb.group({
      // Step 1
      mobileNumber: ['', [Validators.required, Validators.pattern('^[6-9][0-9]{9}$')]],
      station: ['', Validators.required],

      // Step 2
      firmName: ['', Validators.required],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/),
        ],
      ],
      gstNumber: [
        '',
        [
          Validators.required,
          Validators.pattern(
            '^[0-9]{2}[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[1-9a-zA-Z]{1}[zZ][0-9a-zA-Z]{1}$',
          ),
        ],
      ],
      panNumber: [
        '',
        [Validators.required, Validators.pattern('^[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}$')],
      ],
      tariff: ['', Validators.required],
      msme: [''],
      tnebScNo: ['', [Validators.pattern(/^\d{12}$/)]],
      consentToOperate: ['', Validators.required],

      // Step 3
      doorNo: ['', Validators.required],
      surveyFieldNo: ['', Validators.required],
      street: ['', Validators.required],
      state: ['', Validators.required],
      districtId: ['', Validators.required],
      talukId: ['', Validators.required],
      villageId: ['', Validators.required],
      pincode: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]],
      latitude: ['', [Validators.pattern('^-?[0-9]{1,3}(?:\\.[0-9]{1,15})?$')]],
      longitude: ['', [Validators.pattern('^-?[0-9]{1,3}(?:\\.[0-9]{1,15})?$')]],

      // Step 4
      // plantMachineriesAvailability: ['', Validators.required],
      requiredQuantityOfAsh: [''],
      capacity: ['', [Validators.required, Validators.min(1)]],
      ashCategoryDetails: this.fb.array([], Validators.required),
      // ashUnloadingFacility: this.fb.array([]),

      // Step 5 - docs handled via uploadedFileIds

      // Vendors
      vendors: this.fb.array([]),

      // Declaration
      declarationCheck: [false, Validators.requiredTrue],
    });

    this.mobileOtpForm = this.createMobileOtpForm();
    this.emailOtpForm = this.createEmailOtpForm();
  }

  private createMobileOtpForm(): FormGroup {
    const controls: Record<string, unknown[]> = {};
    for (const field of this.mobileOtpFields) {
      controls[field] = ['', [Validators.required, Validators.pattern(/^\d$/)]];
    }
    return this.fb.group(controls);
  }

  private createEmailOtpForm(): FormGroup {
    const controls: Record<string, unknown[]> = {};
    for (const field of this.emailOtpFields) {
      controls[field] = ['', [Validators.required, Validators.pattern(/^\d$/)]];
    }
    return this.fb.group(controls);
  }

  // ─── FormArray Getters ────────────────────────────────────────────────────

  get vendors(): FormArray {
    return this.registerForm.get('vendors') as FormArray;
  }

  get ashCategoryDetails(): FormArray {
    return this.registerForm.get('ashCategoryDetails') as FormArray;
  }

  get ashUnloadingFacility(): FormArray {
    return this.registerForm.get('ashUnloadingFacility') as FormArray;
  }

  // ─── Vendor Management ───────────────────────────────────────────────────

  createVendor(): FormGroup {
    return this.fb.group({
      vendorName: ['', Validators.required],
      purpose: ['', Validators.required],
      mobileNo: ['', Validators.required],
      email: [
        '',
        [Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/)],
      ],
      deliveryAddress: ['', Validators.required],
    });
  }

  addVendor(): void {
    this.vendors.push(this.createVendor());
  }

  removeVendor(index: number): void {
    if (this.vendors.length > 0) {
      const mobileNo = this.vendors.at(index).get('mobileNo')?.value;
      if (mobileNo) {
        this.verifiedVendorMobiles.delete(mobileNo);
      }
      this.vendors.removeAt(index);
    }
  }

  onVendorMobileBlur(index: number): void {
    const mobileCtrl = this.vendors.at(index).get('mobileNo');
    if (!mobileCtrl || mobileCtrl.invalid) return;

    const mobileNo = String(mobileCtrl.value).trim();
    if (this.verifiedVendorMobiles.has(mobileNo)) return;

    // Optional prompt
    Swal.fire({
      title: 'Verify Mobile Number?',
      text: `Would you like to verify ${mobileNo} with an OTP now?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Verify',
      cancelButtonText: 'Not Now',
      customClass: {
        popup: 'premium-swal-popup',
        title: 'premium-swal-title',
        confirmButton: 'premium-swal-btn',
        cancelButton: 'premium-swal-btn',
      },
    }).then((res) => {
      if (res.isConfirmed) {
        this.initiateVendorOtp(mobileNo);
      }
    });
  }

  initiateVendorOtp(mobileNo: string): void {
    Swal.fire({
      title: 'Sending OTP...',
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    this.registerApiService.generateMobileOtp(mobileNo).subscribe({
      next: (res) => {
        Swal.close();
        if (res?.status) {
          this.showVendorOtpModal(mobileNo);
        } else {
          Toast.fire({ icon: 'error', title: res?.message || 'Failed to send OTP.' });
        }
      },
      error: (err) => {
        Swal.close();
        Toast.fire({ icon: 'error', title: err?.error?.message || 'Failed to send OTP.' });
      },
    });
  }

  showVendorOtpModal(mobileNo: string): void {
    let resendCountdown = 60;
    let timerInterval: ReturnType<typeof setInterval> | null = null;

    Swal.fire({
      title: 'Enter OTP',
      html: `
        <p style="margin-bottom: 8px;">An OTP has been sent to ${mobileNo}</p>
        <div id="vendor-resend-container" class="text-center" style="font-size: 13px; margin-top: 10px;">
          <span id="vendor-resend-timer" class="text-muted">Resend OTP in <span id="vendor-countdown-val">${resendCountdown}</span>s</span>
          <a href="javascript:void(0)" id="vendor-resend-btn" class="text-primary fw-semibold" style="display: none; text-decoration: none;">Resend OTP</a>
        </div>
      `,
      input: 'text',
      inputPlaceholder: 'Enter 4-digit OTP',
      inputAttributes: { maxlength: '4', inputmode: 'numeric' },
      showCancelButton: true,
      confirmButtonText: 'Verify',
      confirmButtonColor: '#00796b',
      cancelButtonColor: '#6c757d',
      customClass: {
        popup: 'premium-swal-popup',
        title: 'premium-swal-title',
        confirmButton: 'premium-swal-btn',
        cancelButton: 'premium-swal-btn',
      },
      didOpen: () => {
        const timerEl = document.getElementById('vendor-resend-timer');
        const countdownValEl = document.getElementById('vendor-countdown-val');
        const btnEl = document.getElementById('vendor-resend-btn');

        const startTimer = () => {
          if (timerInterval) clearInterval(timerInterval);
          timerInterval = setInterval(() => {
            resendCountdown--;
            if (resendCountdown > 0) {
              if (countdownValEl) countdownValEl.textContent = resendCountdown.toString();
            } else {
              if (timerInterval) clearInterval(timerInterval);
              if (timerEl) timerEl.style.display = 'none';
              if (btnEl) btnEl.style.display = 'inline-block';
            }
          }, 1000);
        };

        startTimer();

        if (btnEl && timerEl && countdownValEl) {
          btnEl.addEventListener('click', () => {
            btnEl.style.display = 'none';
            timerEl.style.display = 'inline-block';
            countdownValEl.textContent = '...';

            this.registerApiService.resendMobileOtp(mobileNo).subscribe({
              next: (res) => {
                if (res?.status) {
                  resendCountdown = 60;
                  countdownValEl.textContent = resendCountdown.toString();
                  startTimer();
                  Toast.fire({ icon: 'success', title: 'OTP resent successfully!' });
                } else {
                  btnEl.style.display = 'inline-block';
                  timerEl.style.display = 'none';
                  Toast.fire({ icon: 'error', title: res?.message || 'Failed to resend OTP' });
                }
              },
              error: (err) => {
                btnEl.style.display = 'inline-block';
                timerEl.style.display = 'none';
                Toast.fire({ icon: 'error', title: err?.error?.message || 'Failed to resend OTP' });
              },
            });
          });
        }
      },
      willClose: () => {
        if (timerInterval) clearInterval(timerInterval);
      },
      preConfirm: (otp) => {
        if (!/^\d{4}$/.test(otp || '')) {
          Swal.showValidationMessage('Please enter a valid 4-digit OTP.');
          return false;
        }
        return this.registerApiService
          .verifyMobileOtp(mobileNo, otp)
          .toPromise()
          .then((res: any) => {
            if (res?.status) {
              return true;
            }
            throw new Error(res?.message || 'Invalid OTP');
          })
          .catch((err) => {
            Swal.showValidationMessage(
              err.message || err.error?.message || 'OTP verification failed',
            );
            return false;
          });
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.verifiedVendorMobiles.add(mobileNo);
        Toast.fire({ icon: 'success', title: 'Vendor mobile number verified successfully!' });
        this.cdr.detectChanges();
      }
    });
  }

  // ─── Ash Category Multi-Select ───────────────────────────────────────────

  minMaxQtyValidator(group: FormGroup): { [key: string]: boolean } | null {
    const min = group.get('minLoad')?.value;
    const max = group.get('maxLoad')?.value;
    if (min !== null && max !== null && min !== '' && max !== '') {
      if (Number(min) > Number(max)) {
        return { minGreaterThanMax: true };
      }
    }
    return null;
  }

  isAshCategoryChecked(categoryId: any): boolean {
    const formArray = this.registerForm.get('ashCategoryDetails') as FormArray;
    return formArray.controls.some((ctrl) => String(ctrl.value.categoryId) === String(categoryId));
  }

  toggleAshCategory(categoryId: any) {
    const formArray = this.registerForm.get('ashCategoryDetails') as FormArray;
    const idx = formArray.controls.findIndex(
      (ctrl) => String(ctrl.value.categoryId) === String(categoryId),
    );
    if (idx > -1) {
      formArray.removeAt(idx);
    } else {
      formArray.push(
        this.fb.group(
          {
            categoryId: [categoryId],
            minLoad: [
              '',
              [
                Validators.required,
                Validators.pattern('^[0-9]{1,5}$'),
                Validators.min(1),
                Validators.max(10),
              ],
            ],
            maxLoad: [
              '',
              [
                Validators.required,
                Validators.pattern('^[0-9]{1,5}$'),
                Validators.min(1),
                Validators.max(20),
              ],
            ],
          },
          {
            validators: this.minMaxQtyValidator.bind(this),
          },
        ),
      );
    }
  }

  getCategoryFormGroup(categoryId: any): FormGroup | null {
    const formArray = this.registerForm.get('ashCategoryDetails') as FormArray;
    const ctrl = formArray.controls.find((c) => String(c.value.categoryId) === String(categoryId));
    return ctrl ? (ctrl as FormGroup) : null;
  }

  getCategoryName(categoryId: number): string {
    return (
      this.ashCategories.find((c) => String(c.id) === String(categoryId))?.name ||
      String(categoryId)
    );
  }

  // ─── Ash Unloading Facility Multi-Select ─────────────────────────────────

  isUnloadSelected(id: number): boolean {
    return this.selectedUnloadIds.includes(id);
  }

  toggleUnload(item: any, event: MouseEvent): void {
    event.stopPropagation();
    const idx = this.selectedUnloadIds.indexOf(item.id);
    if (idx >= 0) {
      this.selectedUnloadIds.splice(idx, 1);
    } else {
      this.selectedUnloadIds.push(item.id);
    }
    this.cdr.detectChanges();
  }

  getUnloadName(id: number): string {
    return this.ashUnloadList.find((u) => u.id === id)?.name || String(id);
  }

  removeUnload(id: number): void {
    this.selectedUnloadIds = this.selectedUnloadIds.filter((u) => u !== id);
    this.cdr.detectChanges();
  }

  // ─── Stepper ─────────────────────────────────────────────────────────────

  toggleStepper(): void {
    this.showStepper = !this.showStepper;
  }

  isStepCompleted(stepIndex: number): boolean {
    return this.activeStep > stepIndex;
  }

  getProgressPercent(): number {
    return (this.activeStep / (this.totalSteps - 1)) * 100;
  }

  ngAfterViewInit() {
    // Enable sidebar transitions after first paint
    setTimeout(() => {
      this.stepperReady = true;
      this.cdr.detectChanges();
      this.refreshMapSections();
    }, 0);
  }

  setStep(stepIndex: number): void {
    if (stepIndex > this.activeStep) {
      if (!this.validateCurrentStep()) {
        Toast.fire({ icon: 'warning', title: 'Please complete the current step first.' });
        return;
      }
    }
    this.activeStep = stepIndex;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.refreshMapSections();
  }

  nextStep(): void {
    this.markCurrentStepTouched();
    if (this.validateCurrentStep()) {
      this.saveDraftForCurrentStep(); // save draft silently before advancing
      if (this.activeStep < this.totalSteps - 1) {
        this.activeStep++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.refreshMapSections();
      }
    } else {
      Toast.fire({ icon: 'error', title: 'Please complete all required fields.' });
    }
  }

  prevStep(): void {
    if (this.activeStep > 0) {
      this.activeStep--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.refreshMapSections();
    }
  }

  private validateCurrentStep(): boolean {
    switch (this.activeStep) {
      case 0:
        return !!(this.mobileOtpVerified && this.registerForm.get('station')?.valid);
      case 1: {
        // email may be disabled after OTP verify, so use otpVerifiedEmail flag instead
        const emailOk = !!(this.emailOtpVerified && this.otpVerifiedEmail);
        const gstVal = (this.registerForm.get('gstNumber')?.value || '').trim().toUpperCase();
        const panVal = (this.registerForm.get('panNumber')?.value || '').trim().toUpperCase();
        const gstPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        const formValid = !!(
          this.registerForm.get('firmName')?.valid &&
          emailOk &&
          gstPattern.test(gstVal) &&
          panPattern.test(panVal)
        );
        if (!formValid) return false;
        if (
          !this.uploadedFileIds['gstCertificate'] ||
          !this.uploadedFileIds['panCertificate'] ||
          !this.uploadedFileIds['capacityCertificate']
        )
          return false;
        return true;
      }
      case 2:
        return !!(
          this.registerForm.get('doorNo')?.valid &&
          this.registerForm.get('surveyFieldNo')?.valid &&
          this.registerForm.get('street')?.valid &&
          this.registerForm.get('state')?.valid &&
          this.registerForm.get('districtId')?.valid &&
          this.registerForm.get('talukId')?.valid &&
          this.registerForm.get('villageId')?.valid &&
          this.registerForm.get('pincode')?.valid
        );
      case 3:
        return !!(this.registerForm.get('capacity')?.valid && this.ashCategoryDetails.length > 0);
      case 4:
        return !!this.vendors.valid;
      case 5:
        return true; // documents are optional to proceed, server validates required ones
      case 6:
        return this.registerForm.get('declarationCheck')?.value === true;
      default:
        return true;
    }
  }

  private markCurrentStepTouched(): void {
    const touch = (fields: string[]) =>
      fields.forEach((f) => this.registerForm.get(f)?.markAsTouched());
    switch (this.activeStep) {
      case 0:
        touch(['mobileNumber', 'station']);
        break;
      case 1:
        touch(['firmName', 'email', 'gstNumber', 'panNumber', 'tariff']);
        break;
      case 2:
        touch(['doorNo', 'street', 'state', 'districtId', 'talukId']);
        break;
      case 3:
        touch(['capacity']);
        break;
      case 4:
        this.vendors.markAllAsTouched();
        break;
      case 6:
        touch(['declarationCheck']);
        break;
    }
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  ngOnInit(): void {
    document.body.classList.add('register-page');
    this.addVendor();
    this.loadPowerStations();
    // this.loadTariff();

    this.loadAshUnloadingFacilities();
    this.loadStates();

    // Mobile validation
    this.registerForm.get('mobileNumber')?.valueChanges.subscribe((value) => {
      const mobile = String(value || '');
      if (this.mobileOtpVerified && mobile === this.otpVerifiedMobile) return;
      if (this.mobileOtpVerifying) return;
      if (mobile !== this.otpVerifiedMobile) {
        this.resetMobileOtpState();
      }
      if (mobile.length === 10 && /^[6-9][0-9]{9}$/.test(mobile)) {
        this.mobileCheckStatus = 'checking';
        this.cdr.detectChanges();
        this.registerApiService.checkMobileExists(value).subscribe({
          next: (res) => {
            if (res && res.status === false) {
              this.mobileCheckStatus = 'exists';
              this.mobileCheckErrorMsg = res.message || 'This mobile number is already registered!';
              this.registerForm.get('mobileNumber')?.setErrors({ alreadyRegistered: true });
            } else {
              this.mobileCheckStatus = 'valid';
              this.mobileCheckErrorMsg = '';
              const currentErrors = this.registerForm.get('mobileNumber')?.errors;
              if (currentErrors) {
                delete currentErrors['alreadyRegistered'];
                this.registerForm
                  .get('mobileNumber')
                  ?.setErrors(Object.keys(currentErrors).length === 0 ? null : currentErrors);
              }
            }
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.mobileCheckStatus = 'exists';
            this.mobileCheckErrorMsg = err?.error?.message || 'Error validating mobile number.';
            this.registerForm.get('mobileNumber')?.setErrors({ alreadyRegistered: true });
            this.cdr.detectChanges();
          },
        });
      } else {
        this.mobileCheckStatus = null;
        this.cdr.detectChanges();
      }
    });

    // Email validation & OTP reset
    this.registerForm.get('email')?.valueChanges.subscribe((value) => {
      const email = String(value || '');
      if (this.emailOtpVerified && email === this.otpVerifiedEmail) return;
      if (this.emailOtpVerifying) return;
      if (email !== this.otpVerifiedEmail) {
        this.resetEmailOtpState();
      }
    });

    // Auto uppercase GST and PAN
    // this.registerForm.get('gstNumber')?.valueChanges.subscribe((value) => {
    //   if (value) {
    //     this.registerForm.get('gstNumber')?.setValue(value.toUpperCase(), { emitEvent: false });
    //   }
    // });

    this.registerForm.get('panNumber')?.valueChanges.subscribe((value) => {
      if (value) {
        this.registerForm.get('panNumber')?.setValue(value.toUpperCase(), { emitEvent: false });
      }
    });

    // MSME & Station listeners for Ash Categories
    this.registerForm.get('msme')?.valueChanges.subscribe(() => {
      this.checkAndLoadAshCategories();
    });
    this.registerForm.get('station')?.valueChanges.subscribe(() => {
      this.checkAndLoadAshCategories();
    });

    // State → District cascade
    this.registerForm.get('state')?.valueChanges.subscribe((stateId) => {
      this.districts = [];
      this.taluks = [];
      this.registerForm.get('districtId')?.setValue('');
      this.registerForm.get('talukId')?.setValue('');
      if (stateId) this.loadDistricts(stateId);
    });

    // District → Taluk cascade
    this.registerForm.get('districtId')?.valueChanges.subscribe((districtId) => {
      this.taluks = [];
      this.villages = [];
      this.registerForm.get('talukId')?.setValue('');
      this.registerForm.get('villageId')?.setValue('');
      if (districtId) this.loadTaluks(districtId);
    });

    // Taluk → Village cascade
    this.registerForm.get('talukId')?.valueChanges.subscribe((talukId) => {
      this.villages = [];
      this.registerForm.get('villageId')?.setValue('');
      if (talukId) this.loadVillages(talukId);
    });

    // Close dropdowns on outside click
    window.addEventListener('click', this.closeDropdowns);

    setTimeout(() => {
      this.stepperReady = true;
      this.cdr.detectChanges();
    }, 50);
  }

  ngOnDestroy(): void {
    document.body.classList.remove('register-page');
    this.clearMobileOtpResendTimer();
    window.removeEventListener('click', this.closeDropdowns);
  }

  private closeDropdowns = () => {
    let changed = false;
    if (this.showCategoryDropdown) {
      this.showCategoryDropdown = false;
      changed = true;
    }

    if (this.showUnloadDropdown) {
      this.showUnloadDropdown = false;
      changed = true;
    }
    if (changed) this.cdr.detectChanges();
  };

  // ─── API Loaders ─────────────────────────────────────────────────────────

  private extractData(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.list)) return res.data.list;
    if (res?.data?.list && typeof res.data.list === 'object') {
      return Object.keys(res.data.list).map((k) => ({ id: Number(k), name: res.data.list[k] }));
    }
    return [];
  }

  loadPowerStations(): void {
    this.stationsLoading = true;
    this.registerApiService
      .getPowerStations()
      .pipe(
        finalize(() => {
          this.stationsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.powerStations = this.extractData(res);
        },
      });
  }

  // loadTariff(): void {
  //   this.tariffLoading = true;
  //   this.registerApiService
  //     .getTariff()
  //     .pipe(
  //       finalize(() => {
  //         this.tariffLoading = false;
  //         this.cdr.detectChanges();
  //       }),
  //     )
  //     .subscribe({
  //       next: (res) => {
  //         this.tariffList = this.extractData(res);
  //       },
  //     });
  // }

  loadAshCategories(stationId: string | number, msme?: string): void {
    this.categoriesLoading = true;
    this.registerApiService
      .getAshCategories(stationId, msme, [])
      .pipe(
        finalize(() => {
          this.categoriesLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.ashCategories = this.extractData(res);
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Error loading ash categories:', err);
          this.ashCategories = [];
        },
      });
  }

  checkAndLoadAshCategories(): void {
    const stationId = this.registerForm.get('station')?.value;
    const msme = this.registerForm.get('msme')?.value;

    if (stationId) {
      this.loadAshCategories(stationId, msme);
    } else {
      this.ashCategories = [];
      this.ashCategoryDetails.clear();
      this.cdr.detectChanges();
    }
  }

  loadAshUnloadingFacilities(): void {
    this.unloadLoading = true;
    this.registerApiService
      .getAshUnloadingFacilities()
      .pipe(
        finalize(() => {
          this.unloadLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.ashUnloadList = this.extractData(res);
        },
      });
  }

  loadStates(): void {
    this.statesLoading = true;
    this.registerApiService
      .getStates()
      .pipe(
        finalize(() => {
          this.statesLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.states = this.extractData(res);
          const tn = this.states.find(
            (s) => s.name?.toLowerCase().replace(/\s/g, '') === 'tamilnadu',
          );
          if (tn) this.registerForm.patchValue({ state: tn.id });
        },
      });
  }

  loadDistricts(stateId: string | number): void {
    this.districtsLoading = true;
    this.registerApiService
      .getDistricts(stateId)
      .pipe(
        finalize(() => {
          this.districtsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.districts = this.extractData(res);
        },
      });
  }

  loadTaluks(districtId: string | number): void {
    this.taluksLoading = true;
    this.registerApiService
      .getTaluks(districtId)
      .pipe(
        finalize(() => {
          this.taluksLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.taluks = this.extractData(res);
        },
      });
  }

  loadVillages(talukId: string | number): void {
    this.villagesLoading = true;
    this.registerApiService
      .getVillages(talukId)
      .pipe(
        finalize(() => {
          this.villagesLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          this.villages = this.extractData(res);
        },
      });
  }

  // ─── File Upload ─────────────────────────────────────────────────────────

  triggerFileInput(docKey: string): void {
    this.activeUploadDocKey = docKey;
    const input = document.getElementById(`fileUpload-${docKey}`) as HTMLInputElement;
    if (input) input.click();
  }

  onFileSelected(event: Event, docKey: string): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.activeUploadDocKey = docKey;
    this.selectedFileForUpload = file;
    this.confirmUpload();
    input.value = '';
  }

  confirmUpload(): void {
    if (!this.selectedFileForUpload || !this.activeUploadDocKey) return;
    const docKey = this.activeUploadDocKey;
    const docTypeId = this.documentTypeIds[docKey];
    if (!docTypeId) {
      Toast.fire({ icon: 'error', title: 'Invalid document type ID' });
      return;
    }
    this.isUploading = true;
    this.cdr.detectChanges();
    this.registerApiService
      .uploadDocument(docTypeId, this.selectedFileForUpload)
      .pipe(
        finalize(() => {
          this.isUploading = false;
          this.activeUploadDocKey = null;
          this.selectedFileForUpload = null;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const docId = Number(
            res?.data?.id ?? res?.data?.documentId ?? res?.data?.documentUploadId ?? res?.id ?? 0,
          );
          if (!docId) {
            Toast.fire({ icon: 'error', title: 'Invalid response from server' });
            return;
          }
          this.uploadedFiles[docKey] = this.selectedFileForUpload;
          this.uploadedFileIds[docKey] = docId.toString();
          Toast.fire({ icon: 'success', title: 'Document uploaded successfully' });
        },
        error: () => Toast.fire({ icon: 'error', title: 'Failed to upload document' }),
      });
  }

  previewDocumentUrl: SafeResourceUrl | null = null;
  previewDownloadUrl: string | null = null;
  previewDownloadName: string | null = null;

  viewDocument(docKey: string): void {
    const file = this.uploadedFiles[docKey];
    if (file) {
      const url = URL.createObjectURL(file);
      this.previewDownloadUrl = url;
      this.previewDownloadName = file.name;
      this.previewDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      const uploadId = this.uploadedFileIds[docKey];
      if (uploadId) {
        this.registerApiService.downloadDocument(uploadId).subscribe({
          next: (blob: Blob) => {
            let type = blob.type;
            if (!type || type === 'application/octet-stream' || type === 'application/json') {
              type = 'application/pdf';
            }
            const typedBlob = new Blob([blob], { type: type });
            const url = window.URL.createObjectURL(typedBlob);

            this.previewDownloadUrl = url;
            this.previewDownloadName = this.docLabels[docKey] + '.pdf';
            this.previewDocumentUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
          },
          error: (err: any) => {
            console.error('Failed to download document:', err);
            const Toast = Swal.mixin({
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
            Toast.fire({ icon: 'error', title: 'Failed to download document for preview' });
          },
        });
      }
    }
  }

  closeDocumentPreview(): void {
    this.previewDocumentUrl = null;
    this.previewDownloadUrl = null;
    this.previewDownloadName = null;
  }

  downloadPreviewDocument() {
    if (this.previewDownloadUrl && this.previewDownloadName) {
      const a = document.createElement('a');
      a.href = this.previewDownloadUrl;
      a.download = this.previewDownloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  deleteDocument(docKey: string): void {
    this.uploadedFiles[docKey] = null;
    this.uploadedFileIds[docKey] = null;
  }

  isFieldActive(fieldName: string): boolean {
    const c = this.registerForm.get(fieldName);
    return !!(c && (c.value || c.dirty || c.touched));
  }

  // ─── Draft Save ────────────────────────────────────────────────────────────

  saveDraftForCurrentStep(): void {
    const mobileNo = this.otpVerifiedMobile || this.getMobileNumber();
    if (!mobileNo) return;

    const val = this.registerForm.getRawValue();
    let formData: Record<string, unknown> = {};

    switch (this.activeStep) {
      case 0: // Step 1: Initial Verification
        formData = {
          powerStationsId: val.station,
          mobileNo,
        };
        break;

      case 1: // Step 2: Firm Details
        formData = {
          firmName: val.firmName,
          email: val.email,
          gstNo: val.gstNumber,
          panNo: val.panNumber,
          documents: Object.keys(this.uploadedFileIds)
            .filter((k) => this.uploadedFileIds[k] != null)
            .map((k) => ({
              documentTypeId: this.documentTypeIds[k],
              documentUploadId: Number(this.uploadedFileIds[k]),
            })),
        };
        break;

      case 2: // Step 3: Address Details
        formData = {
          stateId: val.state,
          districtId: val.districtId,
          talukId: val.talukId,
          villageId: val.villageId,
          doorNo: val.doorNo,
          sfNo: val.surveyFieldNo,
          street: val.street,
          pincode: val.pincode,
          latitude: val.latitude,
          longitude: val.longitude,
        };
        break;

      case 3: // Step 4: Ash Categories
        formData = {
          capacity: val.capacity,
          ashCategoryDetails: (val.ashCategoryDetails || []).map((d: any) => ({
            categoryId: Number(d.categoryId),
            minLoad: Number(d.minLoad),
            maxLoad: Number(d.maxLoad),
          })),
        };
        break;

      case 4: // Step 5: Vendor Details
        formData = {
          vendors: (val.vendors || []).map((v: any) => ({
            vendorName: v.vendorName,
            purpose: v.purpose,
            mobileNo: v.mobileNo,
            email: v.email,
            deliveryAddress: v.deliveryAddress,
          })),
        };
        break;

      case 5: // Step 6: Documents
        formData = {
          documents: Object.keys(this.uploadedFileIds)
            .filter((k) => this.uploadedFileIds[k] != null)
            .map((k) => ({
              documentTypeId: this.documentTypeIds[k],
              documentUploadId: Number(this.uploadedFileIds[k]),
            })),
        };
        break;

      case 6: // Step 7: Declaration
        formData = {
          declarationCheck: val.declarationCheck,
        };
        break;

      default:
        return; // nothing to draft for other steps
    }

    const payload = {
      mobileNo,
      registrationType: 3,
      step: this.activeStep + 1, // backend steps are 1-indexed
      formData,
    };

    this.registerApiService.saveTraderDraftStep(payload).subscribe({
      next: () => {
        /* silent success */
      },
      error: (err) => console.warn('Draft save failed:', err),
    });
  }

  // ─── Draft Save/Resume (Manual) ────────────────────────────────────────────

  canSaveDraft(): boolean {
    const mobileNo = this.otpVerifiedMobile || this.getMobileNumber();
    return /^[6-9][0-9]{9}$/.test(mobileNo);
  }

  onSaveDraft(silent = false): void {
    const mobileNo = this.otpVerifiedMobile || this.getMobileNumber();
    if (!this.canSaveDraft()) {
      if (!silent) {
        Toast.fire({
          icon: 'warning',
          title: 'Enter a valid 10-digit mobile number before saving a draft.',
        });
      }
      return;
    }

    const val = this.registerForm.getRawValue();
    const apiStep = this.activeStep + 1;
    let formData: Record<string, unknown> = {};

    switch (this.activeStep) {
      case 0:
        formData = { powerStationsId: val.station, mobileNo };
        break;
      case 1:
        formData = {
          firmName: val.firmName,
          email: val.email,
          gstNo: val.gstNumber,
          panNo: val.panNumber,
          documents: Object.keys(this.uploadedFileIds)
            .filter((k) => this.uploadedFileIds[k] != null)
            .map((k) => ({
              documentTypeId: this.documentTypeIds[k],
              documentUploadId: Number(this.uploadedFileIds[k]),
            })),
        };
        break;
      case 2:
        formData = {
          stateId: val.state,
          districtId: val.districtId,
          talukId: val.talukId,
          villageId: val.villageId,
          doorNo: val.doorNo,
          sfNo: val.surveyFieldNo,
          street: val.street,
          pincode: val.pincode,
          latitude: val.latitude,
          longitude: val.longitude,
        };
        break;
      case 3:
        formData = {
          capacity: val.capacity,
          ashCategoryDetails: (val.ashCategoryDetails || []).map((d: any) => ({
            categoryId: Number(d.categoryId),
            minLoad: Number(d.minLoad),
            maxLoad: Number(d.maxLoad),
          })),
        };
        break;
      case 4:
        formData = {
          vendors: (val.vendors || []).map((v: any) => ({
            vendorName: v.vendorName,
            purpose: v.purpose,
            mobileNo: v.mobileNo,
            email: v.email,
            deliveryAddress: v.deliveryAddress,
          })),
        };
        break;
      case 5:
        formData = {
          documents: Object.keys(this.uploadedFileIds)
            .filter((k) => this.uploadedFileIds[k] != null)
            .map((k) => ({
              documentTypeId: this.documentTypeIds[k],
              documentUploadId: Number(this.uploadedFileIds[k]),
            })),
        };
        break;
      case 6:
        formData = {
          declarationCheck: val.declarationCheck,
        };
        break;
      default:
        formData = {};
    }

    const payload = { mobileNo, registrationType: 3, step: apiStep, formData };

    if (!silent) this.isSavingDraft = true;
    this.cdr.detectChanges();

    this.registerApiService
      .saveTraderDraftStep(payload)
      .pipe(
        finalize(() => {
          if (!silent) this.isSavingDraft = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res) => {
          if (!silent) {
            Toast.fire({
              icon: 'success',
              title: res?.message || `Draft saved for step ${apiStep}.`,
            });
          }
        },
        error: (err) => {
          if (!silent) {
            let msg = 'Failed to save draft. Please try again.';
            if (err?.error?.message) {
              msg =
                typeof err.error.message === 'object'
                  ? Object.values(err.error.message).join(', ')
                  : err.error.message;
            }
            Toast.fire({ icon: 'error', title: msg });
          }
        },
      });
  }

  onResumeDraft(): void {
    Swal.fire({
      title: 'Resume Draft Registration',
      text: 'Enter the mobile number used while saving your draft.',
      input: 'tel',
      inputLabel: 'Registered Mobile Number',
      inputPlaceholder: 'Enter 10-digit mobile number',
      inputAttributes: { maxlength: '10', inputmode: 'numeric' },
      showCancelButton: true,
      confirmButtonColor: '#00796b',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Load Draft',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'premium-swal-popup',
        title: 'premium-swal-title',
        confirmButton: 'premium-swal-btn',
        cancelButton: 'premium-swal-btn',
      },
      preConfirm: (value) => {
        const mobile = String(value || '').trim();
        if (!/^[6-9][0-9]{9}$/.test(mobile)) {
          Swal.showValidationMessage('Enter a valid 10-digit mobile number.');
          return false;
        }
        return mobile;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        this.fetchAndApplyTraderDraft(String(result.value));
      }
    });
  }

  private fetchAndApplyTraderDraft(mobileNo: string): void {
    this.isLoadingDraft = true;
    this.cdr.detectChanges();
    this.registerApiService
      .getTraderDraftDetail(mobileNo)
      .pipe(
        finalize(() => {
          this.isLoadingDraft = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const data = this.extractTraderDraftData(res);
          if (!data) {
            const msg =
              typeof res?.message === 'string'
                ? res.message
                : 'No saved draft found for this mobile number.';
            Toast.fire({ icon: 'info', title: msg });
            return;
          }
          this.applyTraderDraft(data);
          const successMsg =
            typeof res?.message === 'string'
              ? res.message
              : 'Draft loaded successfully. Continue your registration.';
          Toast.fire({ icon: 'success', title: successMsg });
        },
        error: (err: any) => {
          Toast.fire({
            icon: err?.status === 404 ? 'info' : 'error',
            title:
              err?.error?.message ||
              'Unable to load draft. Please verify the mobile number and try again.',
          });
        },
      });
  }

  private extractTraderDraftData(res: any): any {
    if (!res) return null;

    let merged: any = {};
    let msgObj =
      res.message && typeof res.message === 'object' && !Array.isArray(res.message)
        ? res.message
        : null;
    let currentStep = res.currentStep ?? msgObj?.currentStep ?? 1;

    // First attempt to extract from msgObj
    if (msgObj) {
      const formData = msgObj.data ?? msgObj.formData ?? msgObj.draft;
      if (formData && typeof formData === 'object' && !Array.isArray(formData)) {
        Object.assign(merged, formData);
      }
    }

    // Now fallback/merge from candidate (res.data or res)
    const candidate =
      Array.isArray(res.data) && res.data.length > 0
        ? res.data[0]
        : ((!Array.isArray(res.data) && res.data && typeof res.data === 'object'
            ? res.data
            : null) ??
          res.result ??
          res);

    if (candidate && typeof candidate === 'object') {
      const stepCollections = [
        candidate.steps,
        candidate.stepData,
        candidate.draftData,
        candidate.savedSteps,
        candidate.registrationDraftSteps,
        Array.isArray(candidate.formData) ? candidate.formData : null,
      ].filter(Array.isArray);

      stepCollections.forEach((steps: any[]) => {
        steps.forEach((item: any) => {
          const payload = item?.formData ?? item?.data ?? item;
          if (payload && typeof payload === 'object') {
            try {
              const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
              Object.assign(merged, parsed);
            } catch (e) {}
          }
        });
      });

      // Merge step1, step2, etc. properties from candidate
      Object.keys(candidate).forEach((key) => {
        if (/^step[_-]?\d+$/i.test(key) && candidate[key]) {
          const stepVal = candidate[key];
          if (typeof stepVal === 'object') {
            Object.assign(merged, stepVal);
          } else if (typeof stepVal === 'string') {
            try {
              Object.assign(merged, JSON.parse(stepVal));
            } catch (e) {}
          }
        }
      });

      if (candidate.formData && !Array.isArray(candidate.formData))
        Object.assign(merged, candidate.formData);

      const skip = new Set([
        'steps',
        'stepData',
        'draftData',
        'savedSteps',
        'registrationDraftSteps',
        'formData',
        'status',
        'message',
        'result',
      ]);
      Object.keys(candidate).forEach((k) => {
        if (!skip.has(k) && candidate[k] != null && typeof candidate[k] !== 'object') {
          merged[k] = candidate[k];
        }
      });
    }

    const hasContent =
      merged.mobileNo ||
      merged.powerStationsId ||
      merged.firmName ||
      merged.email ||
      merged.doorNo ||
      merged.gstNo ||
      merged.panNo ||
      (Array.isArray(merged.vendors) && merged.vendors.length) ||
      (Array.isArray(merged.ashCategoryDetails) && merged.ashCategoryDetails.length);

    if (!hasContent) return null;
    merged._currentStep = merged.currentStep ?? currentStep;
    return merged;
  }

  private applyTraderDraft(data: any): void {
    const opts = { emitEvent: false };
    console.log('[TradeRegister] applyTraderDraft data:', JSON.stringify(data, null, 2));

    // Mobile
    const mobileNo = String(data.mobileNo ?? '').trim();
    if (/^[6-9][0-9]{9}$/.test(mobileNo)) {
      this.registerForm.get('mobileNumber')?.setValue(mobileNo, opts);
      this.mobileOtpVerified = true;
      this.otpVerifiedMobile = mobileNo;
      this.mobileCheckStatus = 'valid';
      this.registerForm.get('mobileNumber')?.disable(opts);
    }

    // Step 1: Station
    if (data.powerStationsId) {
      this.registerForm.get('station')?.setValue(Number(data.powerStationsId), opts);
      this.checkAndLoadAshCategories();
    }

    // Step 2: Firm details + Address (patch non-address fields first)
    const patch: any = {};
    if (data.firmName) patch['firmName'] = data.firmName;
    if (data.email) {
      patch['email'] = data.email;
      // We do not auto-verify email from draft, user must verify it
    }
    if (data.gstNo || data.gstNumber) patch['gstNumber'] = data.gstNo || data.gstNumber;
    if (data.panNo || data.panNumber) patch['panNumber'] = data.panNo || data.panNumber;

    // Address fields - handle multiple possible API field name variants
    const doorNo = data.doorNo || data.door_no || data.door;
    const sfNo = data.sfNo || data.surveyFieldNo || data.surveyNo || data.sf_no || data.survey_no;
    const street = data.street || data.streetName || data.street_name;
    const pincode = data.pincode || data.pin || data.zipCode || data.zip;
    const latitude = data.latitude || data.lat;
    const longitude = data.longitude || data.lng || data.lon;

    if (doorNo) patch['doorNo'] = doorNo;
    if (sfNo) patch['surveyFieldNo'] = sfNo;
    if (street) patch['street'] = street;
    if (pincode) patch['pincode'] = pincode;
    if (latitude) patch['latitude'] = latitude;
    if (longitude) patch['longitude'] = longitude;

    if (Object.keys(patch).length) this.registerForm.patchValue(patch, opts);

    // Address cascade: state → district → taluk → village
    // We load each level sequentially, waiting for API to finish before setting next value
    const stateId = data.stateId ? Number(data.stateId) : null;
    const districtId = data.districtId ? Number(data.districtId) : null;
    const talukId = data.talukId ? Number(data.talukId) : null;
    const villageId = data.villageId ? Number(data.villageId) : null;

    if (stateId) {
      this.registerForm.get('state')?.setValue(stateId, opts);
      if (districtId) {
        this.registerApiService.getDistricts(stateId).subscribe({
          next: (res: any) => {
            const items = res?.data ?? res?.result ?? res ?? [];
            this.districts = Array.isArray(items) ? items : [];
            this.cdr.detectChanges();
            // Now set the district value
            setTimeout(() => {
              this.registerForm.get('districtId')?.setValue(districtId, opts);
              this.cdr.detectChanges();
              if (talukId) {
                this.registerApiService.getTaluks(districtId).subscribe({
                  next: (res2: any) => {
                    const items2 = res2?.data ?? res2?.result ?? res2 ?? [];
                    this.taluks = Array.isArray(items2) ? items2 : [];
                    this.cdr.detectChanges();
                    setTimeout(() => {
                      this.registerForm.get('talukId')?.setValue(talukId, opts);
                      this.cdr.detectChanges();
                      if (villageId) {
                        this.registerApiService.getVillages(talukId).subscribe({
                          next: (res3: any) => {
                            const items3 = res3?.data ?? res3?.result ?? res3 ?? [];
                            this.villages = Array.isArray(items3) ? items3 : [];
                            this.cdr.detectChanges();
                            setTimeout(() => {
                              this.registerForm.get('villageId')?.setValue(villageId, opts);
                              this.cdr.detectChanges();
                            }, 100);
                          },
                        });
                      }
                    }, 100);
                  },
                });
              }
            }, 100);
          },
        });
      }
    }

    // Step 3: Vendors
    if (Array.isArray(data.vendors) && data.vendors.length > 0) {
      this.vendors.clear();
      data.vendors.forEach((v: any) => {
        const vendorMobile = v.mobileNo ?? '';
        // If restored from draft, we might want to mark them unverified so they must verify again
        // or if they were verified before, there's currently no backend flag. Let's force verify.
        this.vendors.push(
          this.fb.group({
            vendorName: [v.vendorName ?? ''],
            purpose: [v.purpose ?? ''],
            mobileNo: [vendorMobile],
            email: [v.email ?? ''],
            deliveryAddress: [v.deliveryAddress ?? ''],
          }),
        );
      });
    }

    // Step 4: Capacity + Ash Categories
    if (data.capacity) this.registerForm.get('capacity')?.setValue(data.capacity, opts);
    if (Array.isArray(data.ashCategoryDetails) && data.ashCategoryDetails.length > 0) {
      this.ashCategoryDetails.clear();
      data.ashCategoryDetails.forEach((d: any) => {
        this.ashCategoryDetails.push(
          this.fb.group(
            {
              categoryId: [Number(d.categoryId), Validators.required],
              minLoad: [
                d.minLoad ?? '',
                [
                  Validators.required,
                  Validators.pattern('^[0-9]{1,5}$'),
                  Validators.min(1),
                  Validators.max(10),
                ],
              ],
              maxLoad: [
                d.maxLoad ?? '',
                [
                  Validators.required,
                  Validators.pattern('^[0-9]{1,5}$'),
                  Validators.min(1),
                  Validators.max(20),
                ],
              ],
              minQuantity: [d.minQuantity ?? 0],
              maxQuantity: [d.maxQuantity ?? 0],
            },
            { validators: this.minMaxQtyValidator.bind(this) },
          ),
        );
      });
    }

    // Step 5: Documents
    if (Array.isArray(data.documents) && data.documents.length > 0) {
      data.documents.forEach((doc: any) => {
        const typeId = Number(doc.documentTypeId);
        const uploadId = Number(doc.documentUploadId);
        if (typeId && uploadId) {
          const key = Object.keys(this.documentTypeIds).find(
            (k) => this.documentTypeIds[k] === typeId,
          );
          if (key) {
            this.uploadedFileIds[key] = String(uploadId);
          }
        }
      });
    }

    // Navigate to the last saved step
    const apiStep = Number(data._currentStep) || 1;
    this.activeStep = Math.min(Math.max(apiStep - 1, 0), this.totalSteps - 1);
    this.cdr.detectChanges();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.refreshMapSections();
  }

  // ─── Submission ───────────────────────────────────────────────────────────

  submitRegistration(): void {
    if (!this.registerForm.get('declarationCheck')?.value) {
      Toast.fire({ icon: 'error', title: 'Please accept the declaration to proceed.' });
      return;
    }

    // Check if all vendors are verified
    const unverifiedVendors = this.vendors.controls.filter((ctrl) => {
      const mobile = ctrl.get('mobileNo')?.value;
      return mobile && !this.verifiedVendorMobiles.has(mobile);
    });

    if (unverifiedVendors.length > 0) {
      Toast.fire({
        icon: 'error',
        title: 'Please verify all vendor mobile numbers before submitting.',
      });
      return;
    }

    const val = this.registerForm.getRawValue();

    const capacity = Number(val.capacity) || 0;
    console.log('station:', val.station);
    console.log('stateId:', val.stateId);
    console.log('sfNo:', val.sfNo);

    const payload = {
      registrationType: '3',
      registrationTypeName: 'Trader',
      mobileNo: this.otpVerifiedMobile || val.mobileNumber,
      powerStationsId: val.station,
      firmName: val.firmName,
      email: val.email,
      gstNo: val.gstNumber,
      panNo: val.panNumber,
      // tariff: val.tariff,

      // msme: val.msme || '',
      // tnebScNo: val.tnebScNo || '',
      // ctoDetails: val.consentToOperate || '',
      doorNo: val.doorNo,
      street: val.street,
      stateId: val.state,
      districtId: val.districtId,
      talukId: val.talukId,
      capacity: capacity,

      villageId: val.villageId,
      sfNo: val.surveyFieldNo,
      pincode: val.pincode,
      latitude: val.latitude,
      longitude: val.longitude,
      ashCategories: (val.ashCategoryDetails || []).map((detail: any) => {
        let calcQty = Number(detail.minLoad || 0) * capacity;
        return {
          id: Number(detail.categoryId),
          qty: calcQty,
        };
      }),
      ashCategoryDetails: (val.ashCategoryDetails || []).map((d: any) => {
        const minL = Number(d.minLoad) || 0;
        const maxL = Number(d.maxLoad) || 0;
        let minQ = minL * capacity;
        let maxQ = maxL * capacity;
        return {
          categoryId: Number(d.categoryId),
          minLoad: minL,
          maxLoad: maxL,
          minQuantity: minQ,
          maxQuantity: maxQ,
          quantity: minQ, // fallback for legacy logic
        };
      }),
      plantMachineriesAvailability: val.plantMachineriesAvailability,
      requiredQuantityOfAsh: String(val.requiredQuantityOfAsh || ''),
      ashProducts: [],
      ashUnloadingFacility: this.selectedUnloadIds.map((id) => ({ id })),
      vendors: (val.vendors || []).map((v: any) => ({
        vendorName: v.vendorName,
        purpose: v.purpose,
        mobileNo: v.mobileNo,
        email: v.email,
        deliveryAddress: v.deliveryAddress,
      })),
      documents: Object.keys(this.uploadedFileIds)
        .filter((k) => this.uploadedFileIds[k] != null)
        .map((k) => ({
          documentTypeId: this.documentTypeIds[k],
          documentUploadId: this.uploadedFileIds[k],
        })),
    };

    this.isSubmitting = true;
    this.cdr.detectChanges();
    Toast.fire({ icon: 'info', title: 'Submitting registration...' });

    this.registerApiService
      .createTraderRegistration(payload)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (res?.status) {
            const appNo = res?.data?.applicationNo || res?.data?.result?.applicationNo || 'N/A';
            const refNo = res?.data?.referenceNo || res?.data?.result?.referenceNo || 'N/A';
            Swal.fire({
              title: 'Registration Successful!',
              html: `Your trader registration has been submitted successfully.<br><br><strong>Application No:</strong> ${appNo}<br><strong>Reference No:</strong> ${refNo}`,
              icon: 'success',
              confirmButtonColor: '#2563EB',
            }).then(() => this.router.navigate(['/login']));
          } else {
            Swal.fire('Submission Failed', res?.message || 'Please try again.', 'error');
          }
        },
        error: (err: any) => {
          Swal.fire(
            'Error',
            err?.error?.message || 'An error occurred during submission.',
            'error',
          );
        },
      });
  }

  // ─── OTP Logic ───────────────────────────────────────────────────────────

  private isApiSuccess(res: any): boolean {
    const s = res?.status ?? res?.data?.status;
    return s === true || s === 'true' || s === 1;
  }

  getMobileNumber(): string {
    return String(this.registerForm.get('mobileNumber')?.value || '').trim();
  }

  getMobileOtpValue(): string {
    return this.mobileOtpFields.map((f) => this.mobileOtpForm.get(f)?.value || '').join('');
  }

  private resetMobileOtpState(): void {
    this.mobileOtpSent = false;
    this.mobileOtpVerified = false;
    this.otpVerifiedMobile = '';
    this.mobileOtpGenerating = false;
    this.mobileOtpVerifying = false;
    this.mobileOtpError = '';
    this.mobileOtpSuccess = '';
    this.resetMobileOtpForm();
    this.clearMobileOtpResendTimer();
  }

  private resetMobileOtpForm(): void {
    this.mobileOtpForm.reset();
    setTimeout(() => {
      if (this.mobileOtpFields.length > 0) {
        document.getElementById(this.mobileOtpFields[0])?.focus();
      }
    });
  }

  private startMobileOtpResendCountdown(): void {
    this.clearMobileOtpResendTimer();
    this.mobileOtpResendCountdown = this.MOBILE_OTP_RESEND_SECONDS;
    this.mobileOtpResendTimerId = setInterval(() => {
      this.mobileOtpResendCountdown -= 1;
      if (this.mobileOtpResendCountdown <= 0) this.clearMobileOtpResendTimer();
      this.cdr.detectChanges();
    }, 1000);
  }

  private clearMobileOtpResendTimer(): void {
    if (this.mobileOtpResendTimerId) {
      clearInterval(this.mobileOtpResendTimerId);
      this.mobileOtpResendTimerId = null;
    }
    this.mobileOtpResendCountdown = 0;
  }

  get isMobileOtpComplete(): boolean {
    return this.mobileOtpFields.every((f) => this.mobileOtpForm.get(f)?.valid);
  }

  get canGenerateMobileOtp(): boolean {
    return (
      this.mobileCheckStatus === 'valid' &&
      !this.mobileOtpVerified &&
      !this.mobileOtpGenerating &&
      !this.mobileOtpSent
    );
  }

  onGenerateMobileOtp(): void {
    const mobileNo = this.getMobileNumber();
    if (!mobileNo || this.mobileCheckStatus !== 'valid') return;
    this.mobileOtpError = '';
    this.mobileOtpGenerating = true;
    this.cdr.detectChanges();
    this.registerApiService
      .generateMobileOtp(mobileNo)
      .pipe(
        finalize(() => {
          this.mobileOtpGenerating = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (this.isApiSuccess(res)) {
            this.mobileOtpSent = true;
            this.mobileOtpForm.reset();
            this.registerForm.get('mobileNumber')?.disable({ emitEvent: false });
            this.startMobileOtpResendCountdown();
          } else {
            this.mobileOtpError = 'Unable to generate OTP';
          }
        },
        error: () => {
          this.mobileOtpError = 'Unable to generate OTP';
        },
      });
  }

  onVerifyMobileOtp(): void {
    if (this.mobileOtpVerifying) return;
    this.mobileOtpForm.markAllAsTouched();
    if (!this.isMobileOtpComplete) {
      this.mobileOtpError = 'OTP required';
      return;
    }
    const mobileNo = this.getMobileNumber();
    const otp = this.getMobileOtpValue();
    this.mobileOtpVerifying = true;
    this.cdr.detectChanges();
    this.registerApiService
      .verifyMobileOtp(mobileNo, otp)
      .pipe(
        finalize(() => {
          this.mobileOtpVerifying = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (this.isApiSuccess(res)) {
            this.mobileOtpVerified = true;
            this.otpVerifiedMobile = mobileNo;
            this.mobileOtpError = '';
            this.clearMobileOtpResendTimer();
          } else {
            this.mobileOtpError = 'Invalid OTP';
            this.resetMobileOtpForm();
          }
        },
        error: () => {
          this.mobileOtpError = 'Invalid OTP';
          this.resetMobileOtpForm();
        },
      });
  }

  onResendMobileOtp(): void {
    const mobileNo = this.getMobileNumber();
    if (!mobileNo) return;
    this.mobileOtpResendLoading = true;
    this.registerApiService
      .resendMobileOtp(mobileNo)
      .pipe(
        finalize(() => {
          this.mobileOtpResendLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (this.isApiSuccess(res)) {
            this.resetMobileOtpForm();
            this.startMobileOtpResendCountdown();
          }
        },
      });
  }

  changeMobileNumber(): void {
    this.resetMobileOtpState();
    this.mobileCheckStatus = null;
    this.registerForm.get('mobileNumber')?.enable({ emitEvent: false });
    this.cdr.detectChanges();
  }

  onMobileOtpInput(event: Event, currentField: string, nextField: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    input.value = value;
    this.mobileOtpForm.get(currentField)?.setValue(value);
    if (value && nextField) {
      const next = document.getElementById(nextField) as HTMLInputElement | null;
      next?.focus();
    }

    setTimeout(() => {
      if (this.isMobileOtpComplete) {
        this.onVerifyMobileOtp();
      }
    });
  }

  onMobileOtpKeydown(event: KeyboardEvent, currentField: string, prevField: string): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && prevField) {
      const prev = document.getElementById(prevField) as HTMLInputElement | null;
      prev?.focus();
    }
  }

  onMobileOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, this.mobileOtpFields.length);
    this.mobileOtpFields.forEach((field, index) => {
      this.mobileOtpForm.get(field)?.setValue(digits[index] || '');
    });

    const focusIndex = Math.min(digits.length, this.mobileOtpFields.length - 1);
    const focusInput = document.getElementById(
      this.mobileOtpFields[focusIndex],
    ) as HTMLInputElement | null;
    focusInput?.focus();

    setTimeout(() => {
      if (this.isMobileOtpComplete) {
        this.onVerifyMobileOtp();
      }
    });
  }

  // ─── Email OTP ────────────────────────────────────────────────────────────

  private getEmail(): string {
    return this.registerForm.get('email')?.value;
  }

  private getEmailOtpValue(): string {
    return this.emailOtpFields.map((f) => this.emailOtpForm.get(f)?.value).join('');
  }

  private resetEmailOtpForm(): void {
    this.emailOtpForm.reset();
  }

  private resetEmailOtpState(): void {
    this.emailOtpSent = false;
    this.emailOtpVerified = false;
    this.emailOtpGenerating = false;
    this.emailOtpVerifying = false;
    this.emailOtpError = '';
    this.emailOtpSuccess = '';
    this.otpVerifiedEmail = '';
    this.resetEmailOtpForm();
    this.clearEmailOtpResendTimer();
  }

  private startEmailOtpResendCountdown(): void {
    this.emailOtpResendCountdown = this.EMAIL_OTP_RESEND_SECONDS;
    this.clearEmailOtpResendTimer();
    this.emailOtpResendTimerId = setInterval(() => {
      this.emailOtpResendCountdown--;
      if (this.emailOtpResendCountdown <= 0) {
        this.clearEmailOtpResendTimer();
      }
      this.cdr.detectChanges();
    }, 1000);
  }

  private clearEmailOtpResendTimer(): void {
    if (this.emailOtpResendTimerId) {
      clearInterval(this.emailOtpResendTimerId);
      this.emailOtpResendTimerId = null;
    }
    this.emailOtpResendCountdown = 0;
  }

  get isEmailOtpComplete(): boolean {
    return this.emailOtpFields.every((f) => this.emailOtpForm.get(f)?.valid);
  }

  get canGenerateEmailOtp(): boolean {
    return !!(
      this.registerForm.get('email')?.valid &&
      !this.emailOtpVerified &&
      !this.emailOtpGenerating &&
      !this.emailOtpSent
    );
  }

  onGenerateEmailOtp(): void {
    const email = this.getEmail();
    if (!email || this.registerForm.get('email')?.invalid) return;
    this.emailOtpError = '';
    this.emailOtpGenerating = true;
    this.cdr.detectChanges();
    this.registerApiService
      .generateEmailOtp(email)
      .pipe(
        finalize(() => {
          this.emailOtpGenerating = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (this.isApiSuccess(res)) {
            this.emailOtpSent = true;
            this.emailOtpForm.reset();
            this.registerForm.get('email')?.disable({ emitEvent: false });
            this.startEmailOtpResendCountdown();
          } else {
            this.emailOtpError = 'Unable to generate OTP';
          }
        },
        error: () => {
          this.emailOtpError = 'Unable to generate OTP';
        },
      });
  }

  onVerifyEmailOtp(): void {
    if (this.emailOtpVerifying) return;
    this.emailOtpForm.markAllAsTouched();
    if (!this.isEmailOtpComplete) {
      this.emailOtpError = 'OTP required';
      return;
    }
    const email = this.getEmail();
    const otp = this.getEmailOtpValue();
    this.emailOtpVerifying = true;
    this.cdr.detectChanges();
    this.registerApiService
      .verifyEmailOtp(email, otp)
      .pipe(
        finalize(() => {
          this.emailOtpVerifying = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (this.isApiSuccess(res)) {
            this.emailOtpVerified = true;
            this.otpVerifiedEmail = email;
            this.emailOtpError = '';
            this.clearEmailOtpResendTimer();
            this.cdr.detectChanges();
          } else {
            this.emailOtpError = 'Invalid OTP';
            this.resetEmailOtpForm();
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.emailOtpError = 'Invalid OTP';
          this.resetEmailOtpForm();
        },
      });
  }

  onResendEmailOtp(): void {
    const email = this.getEmail();
    if (!email) return;
    this.emailOtpResendLoading = true;
    this.registerApiService
      .resendEmailOtp(email)
      .pipe(
        finalize(() => {
          this.emailOtpResendLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (res: any) => {
          if (this.isApiSuccess(res)) {
            this.resetEmailOtpForm();
            this.startEmailOtpResendCountdown();
          }
        },
      });
  }

  changeEmailAddress(): void {
    this.resetEmailOtpState();
    this.registerForm.get('email')?.enable({ emitEvent: false });
    this.cdr.detectChanges();
  }

  onEmailOtpInput(event: Event, currentField: string, nextField: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '').slice(-1);
    input.value = value;
    this.emailOtpForm.get(currentField)?.setValue(value);
    if (value && nextField) {
      const next = document.getElementById(nextField) as HTMLInputElement | null;
      next?.focus();
    }

    setTimeout(() => {
      if (this.isEmailOtpComplete) {
        this.onVerifyEmailOtp();
      }
    });
  }

  onEmailOtpKeydown(event: KeyboardEvent, currentField: string, prevField: string): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && prevField) {
      const prev = document.getElementById(prevField) as HTMLInputElement | null;
      prev?.focus();
    }
  }

  onEmailOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, this.emailOtpFields.length);
    this.emailOtpFields.forEach((field, index) => {
      this.emailOtpForm.get(field)?.setValue(digits[index] || '');
    });

    const focusIndex = Math.min(digits.length, this.emailOtpFields.length - 1);
    const focusInput = document.getElementById(
      this.emailOtpFields[focusIndex],
    ) as HTMLInputElement | null;
    focusInput?.focus();

    setTimeout(() => {
      if (this.isEmailOtpComplete) {
        this.onVerifyEmailOtp();
      }
    });
  }

  // ─── Preview Helpers ──────────────────────────────────────────────────────

  getStepLabel(step: number): string {
    const labels = [
      'Mobile & Station',
      'Firm Details',
      'Address Info',
      'Operations & Vendors',
      'Documents',
      'Preview',
    ];
    return labels[step] || '';
  }

  getStateName(): string {
    const stateId = this.registerForm.get('state')?.value;
    return this.states.find((s) => s.id == stateId)?.name || stateId || '-';
  }

  getDistrictName(): string {
    const id = this.registerForm.get('districtId')?.value;
    return this.districts.find((d) => d.id == id)?.name || id || '-';
  }

  getTalukName(): string {
    const id = this.registerForm.get('talukId')?.value;
    return this.taluks.find((t) => t.id == id)?.name || id || '-';
  }

  getVillageName(): string {
    const id = this.registerForm.get('villageId')?.value;
    return this.villages.find((v) => v.id == id)?.name || id || '-';
  }

  getPowerStationName(): string {
    const id = this.registerForm.get('station')?.value;
    return this.powerStations.find((p) => p.id == id)?.name || id || '-';
  }

  // getTariffName(): string {
  //   const id = this.registerForm.get('tariff')?.value;
  //   return this.tariffList.find((t) => t.id == id)?.name || id || '-';
  // }

  getDocumentKeys(): string[] {
    return Object.keys(this.uploadedFileIds).filter((k) => this.uploadedFileIds[k] != null);
  }

  onNumberInput(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/\D/g, '');
    this.registerForm.get(controlName)?.setValue(value);
  }

  // ─── Map Methods ──────────────────────────────────────────────────────────

  detectLocation() {
    if (!navigator.geolocation) {
      Toast.fire({ icon: 'warning', title: 'Geolocation not supported.' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = pos.coords.latitude.toFixed(6);
        const longitude = pos.coords.longitude.toFixed(6);
        this.registerForm.patchValue({ latitude, longitude });
        this.registerForm.get('latitude')?.updateValueAndValidity();
        this.registerForm.get('longitude')?.updateValueAndValidity();
        this.updateMapMarkerFromCoordinates(latitude, longitude);
      },
      () =>
        Toast.fire({
          icon: 'warning',
          title: 'Unable to detect location. Please select on the map.',
        }),
    );
  }

  private scheduleLocationMapsInit(): void {
    if (this.activeStep !== this.addressStepIndex) {
      this.destroyInteractiveLocationMaps();
      return;
    }

    if (this.locationMapInitTimer) {
      clearTimeout(this.locationMapInitTimer);
    }
    this.locationMapInitTimer = setTimeout(() => {
      this.initLocationMaps();
    }, 100);
  }

  private schedulePreviewMapsInit(): void {
    if (this.activeStep !== this.previewStepIndex) {
      this.destroyPreviewLocationMaps();
      return;
    }

    if (this.previewMapInitTimer) {
      clearTimeout(this.previewMapInitTimer);
    }
    this.previewMapInitTimer = setTimeout(() => {
      this.initPreviewMaps();
    }, 100);
  }

  private refreshMapSections(): void {
    this.scheduleLocationMapsInit();
    this.schedulePreviewMapsInit();
  }

  private destroyInteractiveLocationMaps(): void {
    this.locationMap?.destroy();
    this.locationMap = null;
  }

  private destroyPreviewLocationMaps(): void {
    this.previewLocationMap?.destroy();
    this.previewLocationMap = null;
  }

  private initLocationMaps(): void {
    this.googleMapsLocationService
      .load(environment.googleMapsApiKey)
      .then(() => {
        this.locationMapError = '';
        this.destroyInteractiveLocationMaps();

        const container = this.locationMapContainer?.nativeElement;
        if (container) {
          this.locationMap = this.googleMapsLocationService.createInteractiveMap(container, {
            defaultCenter: this.defaultMapCenter,
            defaultZoom: this.defaultMapZoom,
            selectedZoom: this.selectedMapZoom,
            initialCoords: this.getCoordinatesFromForm(),
            onLocationChange: (lat, lng) => this.patchLocationCoordinates(lat, lng),
          });

          const searchInput = this.locationSearch?.nativeElement;
          if (searchInput) {
            this.locationMap.bindSearchInput(searchInput);
          }
        }
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.locationMapError = 'Unable to load Google Maps. Please try again later.';
        this.cdr.detectChanges();
      });
  }

  private initPreviewMaps(): void {
    this.googleMapsLocationService
      .load(environment.googleMapsApiKey)
      .then(() => {
        this.previewMapError = '';
        this.destroyPreviewLocationMaps();

        const container = this.previewMapContainer?.nativeElement;
        if (container) {
          this.previewLocationMap = this.googleMapsLocationService.createPreviewMap(container, {
            defaultCenter: this.defaultMapCenter,
            defaultZoom: this.defaultMapZoom,
            selectedZoom: this.selectedMapZoom,
            coords: this.getCoordinatesFromForm(),
          });
        }
        this.cdr.detectChanges();
      })
      .catch(() => {
        this.previewMapError = 'Unable to load Google Maps preview. Please try again later.';
        this.cdr.detectChanges();
      });
  }

  private getCoordinatesFromForm(): { lat: number; lng: number } | null {
    return this.googleMapsLocationService.parseCoordinates(
      this.registerForm.get('latitude')?.value,
      this.registerForm.get('longitude')?.value,
    );
  }

  private patchLocationCoordinates(lat: number, lng: number): void {
    const latitude = lat.toFixed(6);
    const longitude = lng.toFixed(6);
    this.registerForm.patchValue({ latitude, longitude });
    this.registerForm.get('latitude')?.updateValueAndValidity();
    this.registerForm.get('longitude')?.updateValueAndValidity();
  }

  private updateMapMarkerFromCoordinates(latitude: string, longitude: string): void {
    const coords = this.googleMapsLocationService.parseCoordinates(latitude, longitude);
    if (!coords) {
      return;
    }

    if (!this.locationMap) {
      this.refreshMapSections();
      return;
    }

    this.locationMap.setMarker(coords.lat, coords.lng, true);
  }
}
